import { useEffect, useState, useCallback } from 'react';
import { Calculator, Download, ExternalLink, RefreshCw, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatEUR, formatDateFR } from '../utils';
import type { Prestation } from '../types';

const CALC_PREFILL_KEY = 'cygnature-calc-prefill-v1';

export interface BrouillonImport {
  prop_ttc: number | null;
  artiste_nom: string | null;
  domaine: string | null;
  km: number | null;
  duree_trajet: string | null;
  tarif_ht: number | null;
  marge_pct: number | null;
  frais: number | null;
}

interface Props {
  prestation: Prestation;
  /** Importe les valeurs du brouillon dans le formulaire (sans persister tant que l'utilisateur n'a pas sauvegardé) */
  onImport: (b: BrouillonImport) => void;
}

interface BrouillonRow {
  id: string;
  created_at: string;
  client_nom: string | null;
  date_event: string | null;
  lieu: string | null;
  artiste_nom: string | null;
  domaine: string | null;
  tarif_ht: number | null;
  marge_pct: number | null;
  frais: number | null;
  km: number | null;
  duree_trajet: string | null;
  prop_ttc: number | null;
  prestation_id: string | null;
  converti_le: string | null;
}

export default function BrouillonsLies({ prestation, onImport }: Props) {
  const [rows, setRows] = useState<BrouillonRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    // 1) Brouillons déjà liés à la prestation
    const linked = prestation.id
      ? await supabase
          .from('calculs_brouillons')
          .select('*')
          .eq('prestation_id', prestation.id)
          .order('created_at', { ascending: false })
      : { data: [] as BrouillonRow[], error: null };

    // 2) Brouillons libres correspondant client + date (suggestion)
    const clientStr = prestation.client?.trim();
    let suggested: BrouillonRow[] = [];
    if (clientStr) {
      const { data: sug } = await supabase
        .from('calculs_brouillons')
        .select('*')
        .is('prestation_id', null)
        .ilike('client_nom', `%${clientStr}%`)
        .order('created_at', { ascending: false })
        .limit(10);
      suggested = (sug as BrouillonRow[]) || [];
    }

    const linkedIds = new Set((linked.data as BrouillonRow[] | null)?.map((r) => r.id) || []);
    const merged = [
      ...((linked.data as BrouillonRow[] | null) || []),
      ...suggested.filter((s) => !linkedIds.has(s.id)),
    ];
    setRows(merged);
    setLoading(false);
  }, [prestation.id, prestation.client]);

  useEffect(() => { load(); }, [load]);

  const linkBrouillon = async (b: BrouillonRow) => {
    if (!prestation.id) {
      toast.error('Enregistrez d\'abord la prestation');
      return;
    }
    if (b.prestation_id === prestation.id) return;
    const { error } = await supabase
      .from('calculs_brouillons')
      .update({ prestation_id: prestation.id, converti_le: new Date().toISOString() })
      .eq('id', b.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Brouillon lié à la prestation');
    load();
  };

  const handleImport = (b: BrouillonRow, link: boolean) => {
    onImport({
      prop_ttc: b.prop_ttc,
      artiste_nom: b.artiste_nom,
      domaine: b.domaine,
      km: b.km,
      duree_trajet: b.duree_trajet,
      tarif_ht: b.tarif_ht,
      marge_pct: b.marge_pct,
      frais: b.frais,
    });
    toast.success(`Brouillon importé : ${formatEUR(b.prop_ttc || 0)}`);
    if (link) void linkBrouillon(b);
  };

  const openCalculator = () => {
    // Pré-remplit le calculateur avec la prestation complète
    // (loadFromPrestation chargera les artistes liés et leurs coûts depuis la DB)
    const prefill = {
      prestationId: prestation.id || null,
      prestation, // pour loadFromPrestation côté calculateur
      clientNom: prestation.client,
      dateEvent: prestation.dateEvent,
      lieu: prestation.lieu,
      domaine: prestation.domaines[0] || '',
      artisteNom: prestation.artistes[0] || '',
      km: prestation.km,
      duree: prestation.duree,
    };
    sessionStorage.setItem(CALC_PREFILL_KEY, JSON.stringify(prefill));
    window.open('/crm-pro-evenementiel/artistes-calcul?prefill=1', '_blank');
  };

  const linkedRows = rows.filter((r) => r.prestation_id === prestation.id);
  const suggestedRows = rows.filter((r) => r.prestation_id !== prestation.id);
  const totalLinked = linkedRows.reduce((s, r) => s + (r.prop_ttc || 0), 0);

  return (
    <div className="border border-slate-800 rounded-lg bg-slate-950/40 p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
          <Calculator size={14} strokeWidth={1.5} className="text-amber-400" />
          Calculateur tarif lié
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading}
            className="px-2 py-1 text-[11px] bg-slate-800 hover:bg-slate-700 rounded text-slate-300 flex items-center gap-1">
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} strokeWidth={1.5} />}
            Actualiser
          </button>
          <button type="button" onClick={openCalculator}
            className="px-2 py-1 text-[11px] bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded text-amber-200 flex items-center gap-1">
            <ExternalLink size={11} strokeWidth={1.5} />
            Calculer le tarif
          </button>
        </div>
      </div>

      {linkedRows.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Brouillons liés ({linkedRows.length})</div>
          {linkedRows.map((b) => (
            <BrouillonRow key={b.id} b={b} onImport={() => handleImport(b, false)} variant="linked" />
          ))}
          {linkedRows.length > 1 && (
            <div className="text-[11px] text-amber-300 italic pt-1">
              Total cumulé : <strong>{formatEUR(totalLinked)}</strong>
            </div>
          )}
        </div>
      )}

      {suggestedRows.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Suggestions ({suggestedRows.length})</div>
          {suggestedRows.slice(0, 5).map((b) => (
            <BrouillonRow key={b.id} b={b} onImport={() => handleImport(b, true)} variant="suggested" />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-xs text-slate-500 italic">
          Aucun brouillon. Cliquez sur « Calculer le tarif » pour créer une simulation pré-remplie.
        </div>
      )}
    </div>
  );
}

function BrouillonRow({ b, onImport, variant }: { b: BrouillonRow; onImport: () => void; variant: 'linked' | 'suggested' }) {
  return (
    <div className={`flex items-center justify-between gap-2 px-2.5 py-2 rounded border ${
      variant === 'linked'
        ? 'bg-amber-500/5 border-amber-500/20'
        : 'bg-slate-900/60 border-slate-800'
    }`}>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-200 truncate">
          <span className="font-semibold">{formatEUR(b.prop_ttc || 0)}</span>
          {b.artiste_nom && <span className="text-slate-400"> · {b.artiste_nom}</span>}
          {b.domaine && <span className="text-slate-500"> · {b.domaine}</span>}
        </div>
        <div className="text-[10px] text-slate-500">
          {formatDateFR(b.created_at, { day: '2-digit', month: '2-digit', year: '2-digit' })}
          {b.lieu && ` · ${b.lieu.split(',')[0]}`}
          {b.km != null && ` · ${b.km} km`}
        </div>
      </div>
      <button type="button" onClick={onImport}
        className="shrink-0 px-2 py-1 text-[11px] bg-slate-800 hover:bg-amber-500 hover:text-slate-950 rounded text-slate-200 flex items-center gap-1 transition-colors"
        title={variant === 'linked' ? 'Importer dans le formulaire' : 'Importer + lier à la prestation'}>
        <Download size={11} strokeWidth={1.5} />
        Importer
      </button>
    </div>
  );
}
